import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import PluginService from '__services/PluginService';
import PerformActionService from '__services/PerformActionService';
import { CUSTOM_OBJECTS, DASHBOARD, PLUGINS } from '__queries/query-keys';
import { serviceRefreshAccessStart } from 'store/authentication/authenticationAction';
import { toastVariant, useToast } from '__components/ToastProvider';
import { useTranslation } from 'react-i18next';
import { monitoringExceptionHelper } from 'instrumentation/helpers';
import { MarketplacePlugin } from '__pages/Marketplace/types';
import {
  BulkActionProgress,
  deriveImportOutcome,
  getImportRefetchInterval,
  isAwaitingFailureReport,
  ImportOutcome,
  IMPORT_POLL_INTERVAL_MS,
  IMPORT_POLL_TIMEOUT_MS,
  isProgressUnavailable,
  shouldRefreshObjectCaches,
  shouldRetryImportProgress,
} from './importProgress';

export type ImportProgressState =
  | { phase: 'polling' }
  | { phase: 'done'; outcome: ImportOutcome }
  | null;

type Args = {
  config: MarketplacePlugin;
  businessPluginAppsByApiName: Record<string, any>;
  onImportKZNFileStep?: (pluginAPIName: string, etag?: string) => Promise<void>;
};

export const getKZNFileFlagsFromConfig = (
  config: MarketplacePlugin,
  existingConfig: Record<string, any>
) => {
  const hasKZNFile =
    Boolean(config.base_config?.kzn_schema_file) &&
    Boolean(config.base_config?.kzn_schema_etag);

  const hasImportedKZNFile = Boolean(
    existingConfig?.__kizen_schema_import_etag
  );
  const hasSkippedKZNFile = Boolean(
    existingConfig?.__kizen_schema_import_skipped
  );

  const isImportWizardMode =
    hasKZNFile && !hasImportedKZNFile && !hasSkippedKZNFile;

  const schemaImportIsLatest =
    existingConfig?.__kizen_schema_import_etag ===
    config.base_config?.kzn_schema_etag;

  const schemaImportTs = existingConfig?.__kizen_schema_import_ts;

  const showBlockingImportOnEnable =
    !schemaImportIsLatest && hasKZNFile && !hasSkippedKZNFile;

  return {
    hasKZNFile,
    hasImportedKZNFile,
    hasSkippedKZNFile,
    isImportWizardMode,
    schemaImportIsLatest,
    schemaImportTs,
    showBlockingImportOnEnable,
  };
};

export const usePluginKZNImporter = (args: Args) => {
  const { config, businessPluginAppsByApiName, onImportKZNFileStep } = args;
  const [kznActionPending, setKznActionPending] = useState(false);
  const [trackedImport, setTrackedImport] = useState<{
    progressId: string | null;
    startedAt: number;
  } | null>(null);
  const [showToast] = useToast();
  const { t } = useTranslation();

  const existingConfig = businessPluginAppsByApiName[config.api_name]?.config;

  const {
    isImportWizardMode,
    hasKZNFile,
    hasImportedKZNFile,
    schemaImportIsLatest,
    schemaImportTs,
    hasSkippedKZNFile,
    showBlockingImportOnEnable,
  } = getKZNFileFlagsFromConfig(config, existingConfig);

  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const progressId = trackedImport?.progressId ?? null;
  const startedAt = trackedImport?.startedAt ?? 0;

  const { data: progress, error: progressError } = useQuery<BulkActionProgress>(
    {
      queryKey: PLUGINS.IMPORT_PROGRESS(progressId),
      queryFn: ({ signal }) =>
        PerformActionService.getBulkActionProgressById(progressId, {
          signal,
          timeout: Math.max(1, startedAt + IMPORT_POLL_TIMEOUT_MS - Date.now()),
        }),
      enabled: Boolean(progressId),
      staleTime: Infinity,
      refetchInterval: (query) =>
        trackedImport
          ? getImportRefetchInterval({
              progressId,
              progress: query.state.data,
              progressUnavailable: isProgressUnavailable(query.state.error),
              startedAt,
              now: Date.now(),
            })
          : false,
      retry: (_count, error) =>
        shouldRetryImportProgress(error, startedAt, Date.now()),
      retryDelay: IMPORT_POLL_INTERVAL_MS,
      throwOnError: false,
      notifyOnChangeProps: ['data', 'error', 'dataUpdatedAt'],
    }
  );

  const tracking = {
    progressId,
    progress,
    progressUnavailable: isProgressUnavailable(progressError),
    startedAt,
    now: Date.now(),
  };

  const outcome = trackedImport ? deriveImportOutcome(tracking) : null;

  const importProgress: ImportProgressState = !trackedImport
    ? null
    : outcome && !isAwaitingFailureReport(outcome, tracking)
      ? { phase: 'done', outcome }
      : { phase: 'polling' };

  const needsObjectRefresh =
    outcome?.kind === 'success' && shouldRefreshObjectCaches(outcome.report);

  const refreshStartedForRef = useRef<string | null>(null);

  const [refreshedProgressId, setRefreshedProgressId] = useState<string | null>(
    null
  );

  const refreshingObjects =
    needsObjectRefresh && refreshedProgressId !== progressId;

  useEffect(() => {
    if (
      !needsObjectRefresh ||
      !progressId ||
      refreshStartedForRef.current === progressId
    ) {
      return;
    }

    refreshStartedForRef.current = progressId;

    (async () => {
      try {
        const [error] = await dispatch(serviceRefreshAccessStart() as any);

        if (error) {
          monitoringExceptionHelper(error);
        }

        // Remove rather than invalidate so nothing renders the stale list while refetching
        queryClient.removeQueries({ queryKey: DASHBOARD.CUSTOM_OBJECT_LIST });
        queryClient.removeQueries({ queryKey: CUSTOM_OBJECTS.LIST });
      } catch (error) {
        monitoringExceptionHelper(error);
      } finally {
        setRefreshedProgressId(progressId);
      }
    })();
  }, [needsObjectRefresh, progressId, dispatch, queryClient]);

  const onContinueAfterImport = (onDismissed?: () => void) => {
    setTrackedImport(null);
    onDismissed?.();
  };

  const onImport = useCallback(async () => {
    setKznActionPending(true);
    try {
      const response = await PluginService.performPluginImport(
        config.base_config?.kzn_schema_file,
        businessPluginAppsByApiName[config.api_name]?.id
      );

      setTrackedImport({
        progressId: response?.bulk_action_progress?.id ?? null,
        startedAt: Date.now(),
      });

      await onImportKZNFileStep?.(
        config.api_name,
        config.base_config?.kzn_schema_etag
      );
    } catch (ex: any) {
      showToast({
        message: ex?.message || t('Failed to import Kizen schema file'),
        variant: toastVariant.FAILURE,
      });

      monitoringExceptionHelper(ex);
    } finally {
      setKznActionPending(false);
    }
  }, [config, businessPluginAppsByApiName, showToast, t, onImportKZNFileStep]);

  const onSkip = useCallback(async () => {
    if (kznActionPending) {
      return;
    }

    setKznActionPending(true);

    try {
      await onImportKZNFileStep?.(config.api_name);
    } catch (ex: any) {
      showToast({
        message: ex?.message || t('Failed to skip Kizen schema file import'),
        variant: toastVariant.FAILURE,
      });

      monitoringExceptionHelper(ex);
    } finally {
      setKznActionPending(false);
    }
  }, [config.api_name, kznActionPending, onImportKZNFileStep, showToast, t]);

  return {
    pending:
      (kznActionPending ||
        importProgress?.phase === 'polling' ||
        refreshingObjects) &&
      Boolean(onImportKZNFileStep),
    onImport,
    importProgress,
    refreshingObjects,
    onContinueAfterImport,
    onSkip,
    isImportWizardMode: isImportWizardMode && Boolean(onImportKZNFileStep),
    hasKZNFile: hasKZNFile && Boolean(onImportKZNFileStep),
    hasImportedKZNFile: hasImportedKZNFile && Boolean(onImportKZNFileStep),
    schemaImportIsLatest,
    schemaImportTs,
    hasSkippedKZNFile: hasSkippedKZNFile && Boolean(onImportKZNFileStep),
    showBlockingImportOnEnable:
      showBlockingImportOnEnable && Boolean(onImportKZNFileStep),
  };
};
